import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, CalendarDays, QrCode, Target, ChevronRight, CheckCircle2 } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { pendingCheckins, rememberEventCode } from "../lib/offlineQueue";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { MissionProgress } from "../components/MissionProgress";
import { Button } from "../components/ui/button";
import { localeOf } from "../i18n";

export default function AmbassadorHome() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [next, setNext] = useState(null);
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pendingCheckin, setPendingCheckin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: events }, { data: sum }] = await Promise.all([
          api.get("/events", { params: { scope: "upcoming" } }),
          api.get("/me/summary"),
        ]);
        setSummary(sum);
        const first = events[0] || null;
        setNext(first);
        if (first) {
          const { data } = await api.get(`/events/${first.id}`);
          setDetail(data);
          if (data.short_code) rememberEventCode(data.short_code, data.id);
          const queued = await pendingCheckins();
          setPendingCheckin(queued.some(
            (q) => q.event_id === data.id
              || (!q.event_id && data.my_participation && !data.my_participation.checked_in_at)));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const locale = localeOf(i18n.language);
  const part = detail?.my_participation;
  const soon = next && new Date(next.starts_at) - new Date() < 1000 * 60 * 60 * 48;
  const missions = detail?.missions || [];
  const done = missions.filter((m) => m.my_submission).length;

  if (loading) return <p className="py-16 text-center text-sm text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div className="vy-stagger space-y-5" data-testid="ambassador-home">
      <div>
        <p className="text-sm font-semibold text-muted-foreground">{t("home.hello")}</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="home-user-name">
          {user?.name}
        </h1>
      </div>

      <section className="vy-card overflow-hidden" data-testid="next-event-card">
        <div className="vy-hero p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-accent">{t("home.nextEvent")}</p>
          {next ? (
            <>
              <h2 className="mt-2 font-display text-2xl font-bold text-white" data-testid="next-event-title">
                {next.title}
              </h2>
              <div className="mt-3 space-y-1.5 text-sm text-white/80">
                <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(next.starts_at, locale)}</p>
                {next.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" />{next.location}</p>}
              </div>
            </>
          ) : (
            <>
              <h2 className="mt-2 font-display text-xl font-bold text-white">{t("home.noEvent")}</h2>
              <p className="mt-2 text-sm text-white/70">{t("home.noEventHint")}</p>
            </>
          )}
        </div>
        {next && (
          <div className="space-y-3 p-5">
            <div className="flex items-center gap-2">
              {part ? <StatusBadge status={part.checked_in_at ? "checked_in" : part.status} testId="home-rsvp-status" />
                : <span data-testid="home-rsvp-status" className="rounded-full bg-muted px-3 py-1 text-xs font-semibold uppercase text-muted-foreground">
                    {t("events.rsvp")}
                  </span>}
            </div>
            {part?.status === "confirmed" && !part.checked_in_at && soon && (
              <Button data-testid="home-checkin-btn" onClick={() => navigate("/checkin")}
                className="h-14 w-full rounded-full bg-accent text-base font-bold text-accent-foreground hover:bg-accent/90 active:scale-95">
                <QrCode className="mr-2 h-5 w-5" />{t("home.checkInNow")}
              </Button>
            )}
            <Button data-testid="home-view-event-btn" variant="outline"
              onClick={() => navigate(`/events/${next.slug || next.id}`)}
              className="h-12 w-full rounded-full font-semibold">
              {t("home.viewEvent")}<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}
      </section>

      {detail?.my_progress && part && part.status !== "cancelled" && (
        <MissionProgress progress={detail.my_progress} testId="home-mission-progress"
          checkinState={part?.checked_in_at ? "confirmed" : pendingCheckin ? "pending_sync" : "none"} />
      )}

      {next && missions.length > 0 && (
        <section className="vy-card p-5" data-testid="home-missions-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold">{t("home.myMissions")}</h3>
            <span className="text-sm font-semibold text-muted-foreground">{done}/{missions.length}</span>
          </div>
          <ul className="mt-4 space-y-2">
            {missions.map((m) => (
              <li key={m.id} data-testid={`home-mission-${m.id}`}
                className="flex items-center gap-3 rounded-xl border border-border p-3">
                {m.my_submission ? <CheckCircle2 className="h-5 w-5 shrink-0 text-[hsl(var(--success))]" />
                  : <Target className="h-5 w-5 shrink-0 text-muted-foreground" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{m.title}</p>
                  <p className="text-xs text-muted-foreground">{m.points} {t("common.points")}</p>
                </div>
                {m.my_submission && <StatusBadge status={m.my_submission.status} />}
              </li>
            ))}
          </ul>
          <Link to={`/events/${next.slug || next.id}`} data-testid="home-missions-link"
            className="mt-4 block text-sm font-bold text-primary">{t("missions.submitProof")} →</Link>
        </section>
      )}

      <section className="vy-card p-5" data-testid="home-progress-card">
        <h3 className="font-display text-lg font-bold">{t("home.progress")}</h3>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[
            { label: t("home.attended"), value: summary?.events_attended ?? 0, testId: "stat-attended" },
            { label: t("home.missions"), value: summary?.missions_approved ?? 0, testId: "stat-missions" },
            { label: t("home.score"), value: summary?.score ?? 0, testId: "stat-score" },
          ].map((s) => (
            <div key={s.testId} data-testid={s.testId} className="rounded-xl bg-muted p-4">
              <p className="font-display text-2xl font-extrabold">{s.value}</p>
              <p className="mt-1 text-xs font-semibold text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
        {summary?.missions_pending > 0 && (
          <p className="mt-3 text-xs font-semibold text-[hsl(var(--warning))]" data-testid="home-pending-count">
            {summary.missions_pending} · {t("home.pending")}
          </p>
        )}
      </section>
    </div>
  );
}
